import {
  ChevronDown,
  Copy,
  GripVertical,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  PhoneCall,
  Trash2,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { NumberInput } from '@/components/molecules/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { cn } from '@/lib/utils';
import {
  REMINDER_CHANNELS,
  type ReminderChannel,
  type TemplateConfig,
} from '@/types/reminder-policy';
import {
  UNIT_OPTIONS,
  type CadenceFormStep,
  type OffsetUnit,
} from '@/components/organisms/settings-reminders/cadence-utils';

interface CadenceStepRowProps {
  step: CadenceFormStep;
  index: number;
  templates?: TemplateConfig[];
  onChange: (next: CadenceFormStep) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  canRemove: boolean;
  readOnly?: boolean;
  error?: string;
}

const DEFAULT_TEMPLATE_VALUE = '__default_template__';

export function CadenceStepRow({
  step,
  index,
  templates = [],
  onChange,
  onRemove,
  onDuplicate,
  expanded,
  onToggleExpanded,
  canRemove,
  readOnly,
  error,
}: CadenceStepRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const selectedTemplate = templates.find(
    (template) => template.channel === step.channel && template.key === step.templateKey,
  );
  const templateLabel = selectedTemplate
    ? templateDisplayName(selectedTemplate)
    : 'Default fallback';
  const hasInvalidTemplate = Boolean(step.templateKey?.trim() && !selectedTemplate);

  function handleChannelChange(channel: ReminderChannel) {
    const templateStillMatches = templates.some(
      (template) => template.channel === channel && template.key === step.templateKey,
    );
    onChange({
      ...step,
      channel,
      templateKey: templateStillMatches ? step.templateKey : '',
    });
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-b border-border bg-background last:border-b-0',
        expanded && 'bg-muted/20',
        error && 'bg-destructive/5',
        isDragging && 'opacity-60 ring-2 ring-primary/40',
      )}
    >
      <div className="flex items-start gap-3 px-3 py-3">
        <button
          type="button"
          className="mt-1 cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
          {...attributes}
          {...listeners}
          disabled={readOnly}
          aria-label="Drag to reorder"
        >
          <GripVertical className="size-4" />
        </button>

        <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xs font-medium text-muted-foreground">
          {index + 1}
        </span>

        <button
          type="button"
          className="min-w-0 flex-1 rounded-md px-1 py-0.5 text-left hover:bg-muted/50"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
        >
          <div className="grid min-w-0 gap-1 md:grid-cols-[150px_minmax(0,1fr)] md:gap-4">
            <span className="text-sm font-medium">
              {formatOffsetLabel(step.offsetValue, step.offsetUnit)}
            </span>
            <span className="flex min-w-0 items-center gap-2">
              <Badge variant="outline" className="shrink-0">
                <ChannelIcon channel={step.channel} />
                {step.channel}
              </Badge>
              <span
                className={cn(
                  'min-w-0 truncate text-sm text-muted-foreground',
                  hasInvalidTemplate && 'text-destructive',
                )}
              >
                {templateLabel}
              </span>
            </span>
          </div>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <MoreHorizontal className="size-4" />
              <span className="sr-only">Step actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onToggleExpanded}>
              <Pencil className="size-4" />
              {expanded ? 'Collapse' : 'Edit'}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onDuplicate} disabled={readOnly}>
              <Copy className="size-4" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" disabled={!canRemove} onSelect={onRemove}>
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onToggleExpanded}
          className="h-8 w-8 shrink-0"
          aria-label={expanded ? 'Collapse step' : 'Expand step'}
        >
          <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
        </Button>
      </div>

      {expanded ? (
        <ExpandedStepForm
          step={step}
          templates={templates}
          onChange={onChange}
          onChannelChange={handleChannelChange}
          readOnly={readOnly}
        />
      ) : null}

      {error && <p className="px-3 pb-3 text-xs text-destructive sm:px-14">{error}</p>}
    </div>
  );
}

function TemplateSelect({
  channel,
  value,
  templates,
  disabled,
  onChange,
}: {
  channel: ReminderChannel;
  value: string;
  templates: TemplateConfig[];
  disabled?: boolean;
  onChange: (templateKey: string) => void;
}) {
  const channelTemplates = getTemplatesForChannel(templates, channel);
  const hasCurrentTemplate = channelTemplates.some((template) => template.key === value);
  const selectValue = value && hasCurrentTemplate ? value : DEFAULT_TEMPLATE_VALUE;

  return (
    <Select
      value={selectValue}
      onValueChange={(next) => onChange(next === DEFAULT_TEMPLATE_VALUE ? '' : next)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full" aria-label="Template">
        <SelectValue placeholder="Default fallback" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={DEFAULT_TEMPLATE_VALUE}>Default fallback</SelectItem>
        {channelTemplates.map((template) => (
          <SelectItem key={`${template.channel}-${template.key}`} value={template.key}>
            {templateDisplayName(template)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ChannelIcon({ channel }: { channel: ReminderChannel }) {
  if (channel === 'EMAIL') return <Mail className="size-3.5 text-muted-foreground" />;
  if (channel === 'VOICE') return <PhoneCall className="size-3.5 text-muted-foreground" />;
  return <MessageSquare className="size-3.5 text-muted-foreground" />;
}

function getTemplatesForChannel(templates: TemplateConfig[], channel: ReminderChannel) {
  const seen = new Set<string>();
  return templates.filter((template) => {
    if (template.channel !== channel || seen.has(template.key)) return false;
    seen.add(template.key);
    return true;
  });
}

function templateDisplayName(template: TemplateConfig) {
  return humanizeKey(template.key);
}

function humanizeKey(key: string) {
  return key
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatOffsetLabel(value: number, unit: OffsetUnit) {
  if (value === 0) return 'At trigger';
  const abs = Math.abs(value);
  const label = UNIT_OPTIONS.find((option) => option.value === unit)?.label ?? unit;
  const singular = abs === 1 ? label.replace(/s$/, '') : label;
  return value < 0 ? `${abs} ${singular} before` : `${abs} ${singular} after`;
}

type TimingDirection = 'before' | 'on' | 'after';

function ExpandedStepForm({
  step,
  templates,
  onChange,
  onChannelChange,
  readOnly,
}: {
  step: CadenceFormStep;
  templates: TemplateConfig[];
  onChange: (next: CadenceFormStep) => void;
  onChannelChange: (channel: ReminderChannel) => void;
  readOnly?: boolean;
}) {
  const direction: TimingDirection =
    step.offsetValue === 0 ? 'on' : step.offsetValue < 0 ? 'before' : 'after';
  const absValue = Math.abs(step.offsetValue);

  function handleDirectionChange(dir: TimingDirection) {
    if (dir === 'on') {
      onChange({ ...step, offsetValue: 0 });
    } else {
      const abs = absValue === 0 ? 1 : absValue;
      onChange({ ...step, offsetValue: dir === 'before' ? -abs : abs });
    }
  }

  function handleAbsChange(raw: number | null) {
    const abs = Math.max(0, raw ?? 0);
    onChange({ ...step, offsetValue: direction === 'before' ? -abs : abs });
  }

  return (
    <div className="border-t border-border bg-muted/20 px-4 py-3">
      <div className="grid grid-cols-[0.6fr_1fr_1.4fr_1fr_2fr] gap-x-2 gap-y-1">
        <span className="text-xs font-medium text-muted-foreground">Amount</span>
        <span className="text-xs font-medium text-muted-foreground">Unit</span>
        <span className="text-xs font-medium text-muted-foreground">Timing</span>
        <span className="text-xs font-medium text-muted-foreground">Channel</span>
        <span className="text-xs font-medium text-muted-foreground">Template</span>

        <NumberInput
          min="0"
          step="1"
          className="h-9"
          value={direction === 'on' ? null : absValue}
          placeholder="—"
          disabled={readOnly || direction === 'on'}
          aria-label="Offset amount"
          emptyValue={0}
          allowDecimal={false}
          onChange={handleAbsChange}
        />

        <Select
          value={step.offsetUnit}
          onValueChange={(value) => onChange({ ...step, offsetUnit: value as OffsetUnit })}
          disabled={readOnly || direction === 'on'}
        >
          <SelectTrigger className="w-full" aria-label="Offset unit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNIT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={direction}
          onValueChange={(value) => handleDirectionChange(value as TimingDirection)}
          disabled={readOnly}
        >
          <SelectTrigger className="w-full" aria-label="Timing direction">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="before">before trigger</SelectItem>
            <SelectItem value="on">at trigger</SelectItem>
            <SelectItem value="after">after trigger</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={step.channel}
          onValueChange={(value) => onChannelChange(value as ReminderChannel)}
          disabled={readOnly}
        >
          <SelectTrigger className="w-full" aria-label="Channel">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REMINDER_CHANNELS.map((channel) => (
              <SelectItem key={channel} value={channel}>
                <span className="inline-flex items-center gap-2">
                  <ChannelIcon channel={channel} />
                  {channel}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <TemplateSelect
          channel={step.channel}
          value={step.templateKey ?? ''}
          templates={templates}
          disabled={readOnly}
          onChange={(templateKey) => onChange({ ...step, templateKey })}
        />
      </div>
    </div>
  );
}
